import { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Clock, CreditCard, Loader2, PenLine, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { buildPublicEstimateDocument } from '@/components/documents/adapters';
import { usePortal } from '../PortalContext';
import { usePortalEstimate } from '../hooks/usePortalEstimates';
import { useIssueEstimateToken } from '../hooks/usePortalActions';
import { toPublicEstimatePayload } from '../documents';
import { estimateStatus } from '../status';
import { PortalStatusBadge } from '../components/PortalStatusBadge';
import { PortalDocumentView } from '../components/PortalDocumentView';
import { PortalLoading } from '../components/PortalLoading';
import { PortalErrorState } from '../components/PortalErrorState';

export default function PortalEstimateDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { slug, company, locale, timezone, formatMoney, formatDate, primaryColor } = usePortal();
  const query = usePortalEstimate(company.id, id);
  const issueToken = useIssueEstimateToken();

  const estimate = query.data ?? null;
  const status = useMemo(() => (estimate ? estimateStatus(estimate, timezone) : null), [estimate, timezone]);

  const doc = useMemo(() => {
    if (!estimate || !status) return null;
    // The office copy shows "Awaiting acceptance" for sent/viewed; the other keys map 1:1.
    const docStatus = status.key === 'awaiting' ? 'sent' : status.key;
    return buildPublicEstimateDocument(toPublicEstimatePayload(estimate, company), locale, { status: docStatus, timezone });
  }, [estimate, status, company, locale, timezone]);

  if (query.isLoading) return <PortalLoading label="Loading quote…" />;
  if (query.error) return <PortalErrorState onRetry={() => void query.refetch()} />;
  if (!estimate || !status || !doc) {
    return <PortalErrorState title="Quote not found" message="This quote isn't available on your account." />;
  }

  const homePath = `/portal/${slug}/home`;

  const reviewAndSign = async () => {
    try {
      const token = await issueToken.mutateAsync(estimate.id);
      const returnTo = encodeURIComponent(`/portal/${slug}/estimates/${estimate.id}`);
      navigate(`/sign/${token}?return=${returnTo}`);
    } catch {
      toast.error('We could not open the signing page. Please try again or contact us.');
    }
  };

  const contact = company.phone ? ` on ${company.phone}` : company.email ? ` at ${company.email}` : '';

  return (
    <div className="space-y-4">
      <Button asChild variant="ghost" size="sm" className="-ml-2 gap-1.5">
        <Link to={homePath}><ArrowLeft className="h-4 w-4" /> Back to your account</Link>
      </Button>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm text-muted-foreground">Quote {estimate.estimate_number}</p>
              <p className="text-2xl font-semibold tabular-nums">{formatMoney(estimate.estimate_amount)}</p>
            </div>
            <PortalStatusBadge status={status} />
          </div>

          <div className="mt-4 text-sm">
            {status.key === 'awaiting' && estimate.can_respond && (
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  Please review the quote below.
                  {estimate.valid_until ? ` It's valid until ${formatDate(estimate.valid_until)}.` : ''}
                  {' '}You can accept and sign it, or let us know it's not for you.
                </p>
                <Button
                  size="lg"
                  className="w-full gap-2 sm:w-auto"
                  style={{ backgroundColor: primaryColor }}
                  disabled={issueToken.isPending}
                  onClick={() => void reviewAndSign()}
                >
                  {issueToken.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />}
                  Review &amp; sign
                </Button>
              </div>
            )}
            {status.key === 'awaiting' && !estimate.can_respond && (
              <p className="text-muted-foreground">
                This quote can't be answered online right now. Contact {company.trading_name || company.name}{contact} if you'd like to go ahead.
              </p>
            )}
            {status.key === 'accepted' && (
              <div className="flex items-start gap-2">
                <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                <p>
                  Accepted{estimate.signer_name ? ` by ${estimate.signer_name}` : ''}
                  {(estimate.signed_at ?? estimate.accepted_at) ? ` on ${formatDate(estimate.signed_at ?? estimate.accepted_at)}` : ''}.
                  <span className="text-muted-foreground"> We'll be in touch to arrange the work.</span>
                </p>
              </div>
            )}
            {status.key === 'accepted' && Number(estimate.deposit_amount ?? 0) > 0 && (
              Number(estimate.deposit_paid_amount ?? 0) >= Number(estimate.deposit_amount) - 0.004 ? (
                <p className="mt-2 flex items-center gap-2 text-success">
                  <CheckCircle className="h-4 w-4 shrink-0" />
                  Deposit of {formatMoney(Number(estimate.deposit_amount))} received.
                </p>
              ) : estimate.deposit_payment_url ? (
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <Button asChild style={{ backgroundColor: primaryColor }}>
                    <a href={estimate.deposit_payment_url}>
                      <CreditCard className="mr-2 h-4 w-4" />
                      Pay deposit {formatMoney(Number(estimate.deposit_amount) - Number(estimate.deposit_paid_amount ?? 0))}
                    </a>
                  </Button>
                  <span className="text-xs text-muted-foreground">Secure card payment processed by Square.</span>
                </div>
              ) : (
                <p className="mt-2 text-muted-foreground">
                  A deposit of {formatMoney(Number(estimate.deposit_amount))} is due. We'll send you a payment link.
                </p>
              )
            )}
            {status.key === 'declined' && (
              <div className="flex items-start gap-2">
                <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <p>
                  Declined{estimate.declined_at ? ` on ${formatDate(estimate.declined_at)}` : ''}.
                  <span className="text-muted-foreground"> Changed your mind? Contact us{contact} and we'll issue a fresh quote.</span>
                </p>
              </div>
            )}
            {status.key === 'expired' && (
              <div className="flex items-start gap-2">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                <p>
                  This quote expired{estimate.valid_until ? ` on ${formatDate(estimate.valid_until)}` : ''}.
                  <span className="text-muted-foreground"> Contact us{contact} for an updated quote.</span>
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <PortalDocumentView doc={doc} />
    </div>
  );
}
